// src/components/PerformanceMap/PerformanceMapViewer.tsx
import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  IconButton,
  Drawer,
  useTheme,
  useMediaQuery,
  Chip,
  Typography,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  CircularProgress,
  Paper,
  Tooltip,
  Menu,
  MenuItem,
  Grid,
  LinearProgress,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Close,
  ArrowBack,
  AccountTree,
  ViewList,
  GridView,
  Timeline,
  Refresh,
  FileDownload,
  TableChart,
  Timeline as TimelineIcon,
  TrendingUp,
  TrendingDown,
  Visibility,
} from '@mui/icons-material';
import { useRecoilValue } from 'recoil';
import { userInformationAtom } from '../../Services/_states/auth.js';
import { AiActions } from '../../Services/_actions/Ai/ai.actions.ts';
import { useSnackBar } from '../FlowBuilder/Common/SnackBar/index.tsx';

// Import view components and types
import {
  PerformanceFlowViewWrapper,
  PerformanceTreeView,
  PerformanceGridView,
  PerformanceListView,
  PerformanceData,
  findConnectedNodes,
  getPerformanceColor,
  getPerformanceLabel,
} from './PerformanceViewModes.tsx';
import { PerformanceSidebar } from './PerformanceSidebar.tsx';
import { exportPerformanceTableToPdf } from './Exportperformancetabletopdf.tsx';
import { exportTableToPdf } from './exportTableToPdf.tsx';

const PerformanceMapViewer: React.FC = () => {
  const { showSnackBar } = useSnackBar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const loggedInUser = useRecoilValue(userInformationAtom);
  const userId = loggedInUser?.id || loggedInUser?.user_id || '';
  const aiActions = AiActions();

  // State
  const [performanceData, setPerformanceData] = useState<PerformanceData[]>([]);
  const [selectedSubject, setSelectedSubject] = useState<string>('');
  const [selectedTopic, setSelectedTopic] = useState<string>('');
  const [selectedSubtopic, setSelectedSubtopic] = useState<string>('');
  const [availableSubjects, setAvailableSubjects] = useState<string[]>([]);
  const [availableTopics, setAvailableTopics] = useState<string[]>([]);
  const [availableSubtopics, setAvailableSubtopics] = useState<string[]>([]);
  const [subjectOptions, setSubjectOptions] = useState<{ label: string; value: string }[]>([]);
  const [viewMode, setViewMode] = useState<'flow' | 'tree' | 'grid' | 'list'>('flow');
  const [selectedNode, setSelectedNode] = useState<'subject' | 'topic' | 'subtopic'>('subject');
  const [detailContent, setDetailContent] = useState<any>(null);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [detailDrawerOpen, setDetailDrawerOpen] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [connectedNodes, setConnectedNodes] = useState<{ prerequisites: string[]; dependents: string[] }>({
    prerequisites: [],
    dependents: [],
  });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  // Track if detail was opened from view click (not sidebar)
  const [detailOpenedFromView, setDetailOpenedFromView] = useState<boolean>(false);

  // Fetch performance data
  useEffect(() => {
    if (userId) {
      fetchPerformanceData();
    }
  }, [userId]);

  const fetchPerformanceData = async () => {
    try {
      setLoading(true);
      const response = await aiActions.getStudentPerformanceWithKnowledgeMap(userId);
      console.log('Performance data fetched:', response.data);
      const data = response.data || [];
      setPerformanceData(data);
      const subjects = [...new Set(data.map((d) => d.subject))].sort();
      setAvailableSubjects(subjects);

      const options = subjects.map((subject) => ({
        label: subject,
        value: subject,
      }));
      setSubjectOptions(options);

      if (subjects.length > 0) {
        const firstSubject = subjects[0];
        setSelectedSubject(firstSubject);
      }
      setLoading(false);
    } catch (err) {
      console.error(err);
      setError('Failed to load performance data');
      showSnackBar('Failed to load performance data', 'error');
      setLoading(false);
    }
  };

  // Update available topics when subject changes
  useEffect(() => {
    if (selectedSubject) {
      const currentData = performanceData.find((d) => d.subject === selectedSubject);
      if (currentData) {
        const topics = currentData.topics.map((t) => t.topic);
        setAvailableTopics(topics);
        setSelectedTopic('');
        setSelectedSubtopic('');
        setSelectedNode('subject');
        setDetailContent(null);
        setAvailableSubtopics([]);
        setConnectedNodes({ prerequisites: [], dependents: [] });
        setDetailOpenedFromView(false);
        // Close detail drawer on subject change
        if (isMobile || isTablet) {
          setDetailDrawerOpen(false);
        }
      }
    }
  }, [selectedSubject, performanceData, isMobile, isTablet]);

  // Update available subtopics when topic changes
  useEffect(() => {
    if (selectedTopic && selectedSubject) {
      const currentData = performanceData.find((d) => d.subject === selectedSubject);
      if (currentData) {
        const topic = currentData.topics.find((t) => t.topic === selectedTopic);
        if (topic) {
          const subtopics = topic.subtopics.map((st) => st.subtopic);
          setAvailableSubtopics(subtopics);
          setSelectedSubtopic('');
          // Don't set detail content or open drawer from sidebar selection
          // Only update the selected subtopic in state
        }
      }
    } else {
      setAvailableSubtopics([]);
    }
  }, [selectedTopic, selectedSubject, performanceData]);

  // Handle subject change
  const handleSubjectChange = (subject: string) => {
    setSelectedSubject(subject);
    setSelectedNode('subject');
    setSelectedTopic('');
    setSelectedSubtopic('');
    setDetailContent(null);
    setConnectedNodes({ prerequisites: [], dependents: [] });
    setDetailOpenedFromView(false);
    if (isMobile || isTablet) {
      setDetailDrawerOpen(false);
      setDrawerOpen(false);
    }
  };

  // Handle topic selection from sidebar - NO AUTO OPEN
  const handleTopicSelect = (topic: string) => {
    setSelectedTopic(topic);
    if (topic) {
      setSelectedNode('topic');
      setSelectedSubtopic('');
      // Clear detail content when selecting from sidebar
      setDetailContent(null);
      setConnectedNodes({ prerequisites: [], dependents: [] });
      setDetailOpenedFromView(false);
      // DO NOT open drawer from sidebar selection
      if (isMobile || isTablet) {
        setDetailDrawerOpen(false);
      }
      
      const currentData = performanceData.find((d) => d.subject === selectedSubject);
      if (currentData) {
        const topicData = currentData.topics.find((t) => t.topic === topic);
        if (topicData) {
          const subtopics = topicData.subtopics.map((st) => st.subtopic);
          setAvailableSubtopics(subtopics);
        }
      }
    } else {
      setSelectedNode('subject');
      setSelectedSubtopic('');
      setDetailContent(null);
      setConnectedNodes({ prerequisites: [], dependents: [] });
      setAvailableSubtopics([]);
      setDetailOpenedFromView(false);
      if (isMobile || isTablet) {
        setDetailDrawerOpen(false);
      }
    }
  };

  // Handle subtopic selection from sidebar - NO AUTO OPEN
  const handleSubtopicSelect = (subtopic: string) => {
    setSelectedSubtopic(subtopic);
    if (subtopic) {
      setSelectedNode('subtopic');
      // Clear detail content when selecting from sidebar
      setDetailContent(null);
      setConnectedNodes({ prerequisites: [], dependents: [] });
      setDetailOpenedFromView(false);
      // DO NOT open drawer from sidebar selection
      if (isMobile || isTablet) {
        setDetailDrawerOpen(false);
      }
      
      const currentData = performanceData.find((d) => d.subject === selectedSubject);
      if (currentData) {
        const connected = findConnectedNodes(currentData, subtopic);
        setConnectedNodes(connected);
      }
    } else {
      setSelectedNode('topic');
      setDetailContent(null);
      setConnectedNodes({ prerequisites: [], dependents: [] });
      setDetailOpenedFromView(false);
      if (isMobile || isTablet) {
        setDetailDrawerOpen(false);
      }
    }
  };

  // Clear all filters
  const handleClearFilters = () => {
    setSelectedTopic('');
    setSelectedSubtopic('');
    setSelectedNode('subject');
    setDetailContent(null);
    setConnectedNodes({ prerequisites: [], dependents: [] });
    setAvailableSubtopics([]);
    setDetailOpenedFromView(false);
    if (isMobile || isTablet) {
      setDetailDrawerOpen(false);
      setDrawerOpen(false);
    }
  };

  const handleReset = () => {
    handleClearFilters();
  };

  // Handle node click from view - THIS OPENS THE DRAWER
  const handleNodeClick = (type: 'topic' | 'subtopic', name: string, data?: any) => {
    if (type === 'topic' && name) {
      setSelectedTopic(name);
      setSelectedSubtopic('');
      setSelectedNode('topic');
      setDetailContent({ type: 'topic', data, name });
      setConnectedNodes({ prerequisites: [], dependents: [] });
      setDetailOpenedFromView(true);
      setDrawerOpen(false);
      
      const currentData = performanceData.find((d) => d.subject === selectedSubject);
      if (currentData) {
        const topic = currentData.topics.find((t) => t.topic === name);
        if (topic) {
          const subtopics = topic.subtopics.map((st) => st.subtopic);
          setAvailableSubtopics(subtopics);
        }
      }
      // OPEN drawer from view click
      if (isMobile || isTablet) {
        setDetailDrawerOpen(true);
      }
    } else if (type === 'subtopic' && name) {
      setSelectedSubtopic(name);
      setSelectedNode('subtopic');
      setDetailOpenedFromView(true);
      
      const currentData = performanceData.find((d) => d.subject === selectedSubject);
      if (currentData) {
        const connected = findConnectedNodes(currentData, name);
        setConnectedNodes(connected);
        setDetailContent({ 
          type: 'subtopic', 
          data, 
          name, 
          topic: selectedTopic,
          connectedNodes: connected,
        });
      }
      setDrawerOpen(false);
      // OPEN drawer from view click
      if (isMobile || isTablet) {
        setDetailDrawerOpen(true);
      }
    } else if (type === 'topic' && !name) {
      handleClearFilters();
    }
  };

  // Get current data
  const currentData = useMemo(() => {
    return performanceData.find((d) => d.subject === selectedSubject);
  }, [performanceData, selectedSubject]);

  // Handle PDF export
  const handleExportPerformancePDF = () => {
    if (!currentData) {
      showSnackBar('No data to export', 'warning');
      return;
    }
    exportPerformanceTableToPdf({
      data: currentData,
      onProgress: (msg) => showSnackBar(msg, 'info'),
      onSuccess: (msg) => showSnackBar(msg, 'success'),
      onError: (msg) => showSnackBar(msg, 'error'),
    });
    handleMenuClose();
  };

  const handleExportKnowledgePDF = () => {
    if (!currentData) {
      showSnackBar('No data to export', 'warning');
      return;
    }
    exportTableToPdf({
      data: currentData,
      onProgress: (msg) => showSnackBar(msg, 'info'),
      onSuccess: (msg) => showSnackBar(msg, 'success'),
      onError: (msg) => showSnackBar(msg, 'error'),
    });
    handleMenuClose();
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Render detail drawer content
  const renderDetailContent = () => {
    if (!detailContent) return null;

    if (detailContent.type === 'topic') {
      return (
        <>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b' }}>
            📚 {detailContent.name}
          </Typography>
          <Box sx={{ mt: 2 }}>
            {detailContent.data?.subtopics?.map((st: any, idx: number) => {
              const score = st.best_score;
              const color = getPerformanceColor(score);
              const conn = findConnectedNodes(currentData!, st.subtopic);
              return (
                <Paper
                  key={idx}
                  sx={{
                    p: 1.5,
                    mb: 1,
                    borderRadius: 2,
                    bgcolor: '#fafbfc',
                    border: `1px solid ${color}40`,
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: '#f0f4ff',
                    },
                  }}
                  onClick={() => {
                    // When clicking a subtopic from topic details, open it
                    const currentData = performanceData.find((d) => d.subject === selectedSubject);
                    if (currentData) {
                      const connected = findConnectedNodes(currentData, st.subtopic);
                      setConnectedNodes(connected);
                      setDetailContent({ 
                        type: 'subtopic', 
                        data: st, 
                        name: st.subtopic, 
                        topic: detailContent.name,
                        connectedNodes: connected,
                      });
                      setSelectedSubtopic(st.subtopic);
                      setSelectedNode('subtopic');
                      setDetailOpenedFromView(true);
                      if (isMobile || isTablet) {
                        setDetailDrawerOpen(true);
                      }
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {st.subtopic}
                    </Typography>
                    <Chip
                      label={score !== null && st.tests_taken > 0 ? `${score}%` : 'N/A'}
                      size="small"
                      sx={{ bgcolor: color, color: '#fff', fontWeight: 600 }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                    <Typography variant="caption" sx={{ color: '#64748b' }}>
                      Tests: {st.tests_taken || 0}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#64748b' }}>
                      Progress: {st.learned_percentage || 0}%
                    </Typography>
                  </Box>
                  {st.learning_status && (
                    <Chip
                      label={st.learning_status}
                      size="small"
                      sx={{
                        mt: 0.5,
                        bgcolor: st.learning_status === 'mastered' ? '#d1fae5' :
                                 st.learning_status === 'in-progress' ? '#fef3c7' :
                                 st.learning_status === 'not-started' ? '#fee2e2' : '#e5e7eb',
                        color: st.learning_status === 'mastered' ? '#065f46' :
                               st.learning_status === 'in-progress' ? '#92400e' :
                               st.learning_status === 'not-started' ? '#991b1b' : '#374151',
                        fontWeight: 600,
                        fontSize: '10px',
                      }}
                    />
                  )}
                  {conn.prerequisites.length > 0 && (
                    <Box sx={{ mt: 0.5 }}>
                      <Typography variant="caption" sx={{ color: '#5b21b6' }}>
                        📚 Prerequisites: {conn.prerequisites.join(', ')}
                      </Typography>
                    </Box>
                  )}
                </Paper>
              );
            })}
          </Box>
        </>
      );
    }

    if (detailContent.type === 'subtopic') {
      const { data: st, name, topic, connectedNodes: conn } = detailContent;
      const score = st?.best_score;
      const color = (st?.tests_taken === 0 || score === null) ? '#94a3b8' : getPerformanceColor(score);

      return (
        <>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b' }}>
            {name}
          </Typography>
          <Chip
            label={`From: ${topic}`}
            size="small"
            sx={{ mt: 1, mb: 2, bgcolor: '#667eea', color: '#fff' }}
          />
          
          <Paper sx={{ p: 2, mb: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                  Tests Taken
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b' }}>
                  {st?.tests_taken || 0}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                  Best Score
                </Typography>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    fontWeight: 700, 
                    color: st?.tests_taken > 0 && score !== null ? color : '#94a3b8'
                  }}
                >
                  {st?.tests_taken > 0 && score !== null ? `${score}%` : 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                  Worst Score
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#94a3b8' }}>
                  {st?.tests_taken > 0 && st?.worst_score !== null ? `${st.worst_score}%` : 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                  Progress
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b' }}>
                  {st?.learned_percentage || 0}%
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" sx={{ color: '#64748b' }}>
              Progress: {st?.learned_percentage || 0}%
            </Typography>
            <LinearProgress
              variant="determinate"
              value={st?.learned_percentage || 0}
              sx={{
                height: 8,
                borderRadius: 4,
                bgcolor: '#e2e8f0',
                '& .MuiLinearProgress-bar': {
                  bgcolor: color === '#94a3b8' ? '#94a3b8' : color,
                  borderRadius: 4,
                },
              }}
            />
          </Box>

          {st?.learning_status && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" sx={{ color: '#64748b' }}>
                Learning Status:
              </Typography>
              <Chip
                label={st.learning_status}
                size="small"
                sx={{
                  ml: 1,
                  bgcolor: st.learning_status === 'mastered' ? '#d1fae5' :
                           st.learning_status === 'in-progress' ? '#fef3c7' :
                           st.learning_status === 'not-started' ? '#fee2e2' : '#e5e7eb',
                  color: st.learning_status === 'mastered' ? '#065f46' :
                         st.learning_status === 'in-progress' ? '#92400e' :
                         st.learning_status === 'not-started' ? '#991b1b' : '#374151',
                  fontWeight: 600,
                }}
              />
            </Box>
          )}

          {st?.last_tested && st?.tests_taken > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" sx={{ color: '#64748b' }}>
                Last Tested: <strong>{st.last_tested}</strong>
              </Typography>
            </Box>
          )}

          {st?.trend && st?.tests_taken > 0 && (
            <Paper sx={{ p: 1.5, mb: 2, bgcolor: '#fefce8', borderRadius: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {st.trend === 'improving' ? (
                  <TrendingUp sx={{ color: '#10b981' }} />
                ) : st.trend === 'declining' ? (
                  <TrendingDown sx={{ color: '#ef4444' }} />
                ) : null}
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Trend: {st.trend}
                </Typography>
              </Box>
            </Paper>
          )}

          {conn && (conn.prerequisites.length > 0 || conn.dependents.length > 0) && (
            <Paper sx={{ p: 2, bgcolor: '#fafbfc', borderRadius: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                🔗 Connected Concepts
              </Typography>
              
              {conn.prerequisites.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" sx={{ color: '#5b21b6', fontWeight: 600 }}>
                    📚 Prerequisites (Before):
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                    {conn.prerequisites.map((p: string, i: number) => (
                      <Chip 
                        key={i} 
                        label={p} 
                        size="small" 
                        sx={{ 
                          bgcolor: '#ede9fe',
                          color: '#5b21b6',
                          fontWeight: 500,
                          border: '1px solid #c4b5fd',
                        }} 
                      />
                    ))}
                  </Box>
                </Box>
              )}
              
              {conn.dependents.length > 0 && (
                <Box>
                  <Typography variant="caption" sx={{ color: '#065f46', fontWeight: 600 }}>
                    🎯 Dependents (After):
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                    {conn.dependents.map((d: string, i: number) => (
                      <Chip 
                        key={i} 
                        label={d} 
                        size="small" 
                        sx={{ 
                          bgcolor: '#d1fae5',
                          color: '#065f46',
                          fontWeight: 500,
                          border: '1px solid #6ee7b7',
                        }} 
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Paper>
          )}

          <Button
            fullWidth
            variant="contained"
            onClick={() => {
              handleNodeClick('subtopic', name, st);
            }}
            sx={{
              mt: 2,
              textTransform: 'none',
              fontWeight: 600,
              bgcolor: '#4f46e5',
              '&:hover': { bgcolor: '#4338ca' },
            }}
          >
            <Visibility sx={{ mr: 0.5, fontSize: 16 }} />
            View in Map
          </Button>
        </>
      );
    }

    return null;
  };

  // Render current view
  const renderView = () => {
    if (!currentData) {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <Typography variant="h6" sx={{ color: '#94a3b8' }}>
            {selectedSubject ? 'No data found' : 'Select a subject'}
          </Typography>
          <Typography variant="body2" sx={{ color: '#cbd5e1' }}>
            {selectedSubject ? 'Try selecting a different subject' : 'Use the sidebar to navigate'}
          </Typography>
        </Box>
      );
    }

    switch (viewMode) {
      case 'flow':
        return (
          <PerformanceFlowViewWrapper
            data={currentData}
            selectedNode={selectedNode}
            selectedTopic={selectedTopic}
            selectedSubtopic={selectedSubtopic}
            onNodeClick={handleNodeClick}
            connectedNodes={connectedNodes}
          />
        );
      case 'tree':
        return (
          <PerformanceTreeView
            data={currentData}
            selectedNode={selectedNode}
            selectedTopic={selectedTopic}
            selectedSubtopic={selectedSubtopic}
            onNodeClick={handleNodeClick}
          />
        );
      case 'grid':
        return (
          <PerformanceGridView
            data={currentData}
            selectedNode={selectedNode}
            selectedTopic={selectedTopic}
            selectedSubtopic={selectedSubtopic}
            onNodeClick={handleNodeClick}
          />
        );
      case 'list':
        return (
          <PerformanceListView
            data={currentData}
            selectedNode={selectedNode}
            selectedTopic={selectedTopic}
            selectedSubtopic={selectedSubtopic}
            onNodeClick={handleNodeClick}
          />
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '90vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        height: { sm: '100vh', xs: '100%' },
        bgcolor: '#f8fafc',
        overflow: 'hidden',
      }}
    >
      {/* Sidebar */}
      <Box
        sx={{
          width: { sm: 320, md: 360 },
          height: { sm: '100%', xs: 'auto' },
          overflowY: 'auto',
          bgcolor: '#ffffff',
          borderRight: { sm: '1px solid #e2e8f0', xs: 'none' },
          borderBottom: { sm: 'none', xs: '1px solid #e2e8f0' },
          display: { xs: 'none', sm: 'block' },
          flexShrink: 0,
        }}
      >
        <PerformanceSidebar
          error={error}
          setError={setError}
          availableSubjects={availableSubjects}
          selectedSubject={selectedSubject}
          setSelectedSubject={handleSubjectChange}
          subjectOptions={subjectOptions}
          viewMode={viewMode}
          setViewMode={setViewMode}
          currentData={currentData}
          selectedNode={selectedNode}
          selectedTopic={selectedTopic}
          selectedSubtopic={selectedSubtopic}
          setSelectedNode={setSelectedNode}
          setSelectedTopic={setSelectedTopic}
          setSelectedSubtopic={setSelectedSubtopic}
          setDetailContent={setDetailContent}
          onReset={handleReset}
          isMobile={isMobile}
          loading={loading}
          availableTopics={availableTopics}
          availableSubtopics={availableSubtopics}
          onTopicChange={handleTopicSelect}
          onSubtopicChange={handleSubtopicSelect}
          onClearFilters={handleClearFilters}
        />
      </Box>

      {/* Main Content */}
      <Box
        sx={{
          flex: 1,
          height: { sm: '100%', xs: 'calc(100vh - 60px)' },
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
        }}
      >
        {/* Mobile Header */}
        <Box
          sx={{
            display: { xs: 'flex', sm: 'none' },
            p: 1.5,
            bgcolor: '#fff',
            borderBottom: '1px solid #e2e8f0',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton onClick={() => setDrawerOpen(true)}>
              <MenuIcon />
            </IconButton>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {selectedSubject || 'Performance Map'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {currentData && (
              <Chip
                label={`Class ${currentData.class}`}
                size="small"
                sx={{ bgcolor: '#667eea', color: '#fff' }}
              />
            )}
            <Tooltip title="Export">
              <IconButton size="small" onClick={handleMenuOpen}>
                <FileDownload fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Export Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <MenuItem onClick={handleExportPerformancePDF}>
            <TimelineIcon sx={{ mr: 1, fontSize: 18 }} />
            Export Performance Table
          </MenuItem>
          <MenuItem onClick={handleExportKnowledgePDF}>
            <TableChart sx={{ mr: 1, fontSize: 18 }} />
            Export Knowledge Map
          </MenuItem>
        </Menu>

        {/* View Renderer */}
        <Box sx={{ flex: 1, overflow: 'hidden' }}>{renderView()}</Box>

        {/* Mobile Bottom Bar */}
        {currentData && (
          <Box
            sx={{
              display: { xs: 'flex', sm: 'none' },
              p: 1,
              bgcolor: '#fff',
              borderTop: '1px solid #e2e8f0',
              gap: 1,
              justifyContent: 'space-between',
              alignItems: 'center',
              flexShrink: 0,
            }}
          >
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(_, val) => val && setViewMode(val)}
              size="small"
              sx={{
                '& .MuiToggleButton-root': {
                  px: 1,
                  py: 0.5,
                  fontSize: 10,
                  '&.Mui-selected': {
                    bgcolor: '#667eea',
                    color: '#fff',
                    borderColor: '#667eea',
                  },
                },
              }}
            >
              <ToggleButton value="flow"><AccountTree fontSize="small" /></ToggleButton>
              <ToggleButton value="tree"><Timeline fontSize="small" /></ToggleButton>
              <ToggleButton value="grid"><GridView fontSize="small" /></ToggleButton>
              <ToggleButton value="list"><ViewList fontSize="small" /></ToggleButton>
            </ToggleButtonGroup>

            {selectedNode !== 'subject' && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<ArrowBack />}
                onClick={() => {
                  if (selectedNode === 'subtopic') {
                    setSelectedNode('topic');
                    setSelectedSubtopic('');
                    setDetailContent(null);
                    setConnectedNodes({ prerequisites: [], dependents: [] });
                    setDetailOpenedFromView(false);
                    const currentData = performanceData.find((d) => d.subject === selectedSubject);
                    if (currentData) {
                      const topicData = currentData.topics.find((t) => t.topic === selectedTopic);
                      if (topicData) {
                        setDetailContent({ type: 'topic', data: topicData, name: selectedTopic });
                        setDetailOpenedFromView(true);
                      }
                    }
                    if (isMobile || isTablet) {
                      setDetailDrawerOpen(true);
                    }
                  } else if (selectedNode === 'topic') {
                    handleClearFilters();
                  }
                }}
                sx={{ textTransform: 'none' }}
              >
                Back
              </Button>
            )}
          </Box>
        )}
      </Box>

      {/* Mobile Sidebar Drawer */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: 300,
            maxWidth: '80vw',
          },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
          <IconButton onClick={() => setDrawerOpen(false)}>
            <Close />
          </IconButton>
        </Box>
        <PerformanceSidebar
          error={error}
          setError={setError}
          availableSubjects={availableSubjects}
          selectedSubject={selectedSubject}
          setSelectedSubject={handleSubjectChange}
          subjectOptions={subjectOptions}
          viewMode={viewMode}
          setViewMode={setViewMode}
          currentData={currentData}
          selectedNode={selectedNode}
          selectedTopic={selectedTopic}
          selectedSubtopic={selectedSubtopic}
          setSelectedNode={setSelectedNode}
          setSelectedTopic={setSelectedTopic}
          setSelectedSubtopic={setSelectedSubtopic}
          setDetailContent={setDetailContent}
          onReset={handleReset}
          isMobile={isMobile}
          loading={loading}
          availableTopics={availableTopics}
          availableSubtopics={availableSubtopics}
          onTopicChange={handleTopicSelect}
          onSubtopicChange={handleSubtopicSelect}
          onClearFilters={handleClearFilters}
        />
      </Drawer>

      {/* Detail Drawer - Mobile Bottom Sheet - ONLY opens from view clicks */}
      <Drawer
        anchor="bottom"
        open={detailDrawerOpen}
        onClose={() => {
          setDetailDrawerOpen(false);
          setDetailOpenedFromView(false);
        }}
        PaperProps={{
          sx: {
            maxHeight: '75vh',
            minHeight: '40vh',
            borderRadius: '20px 20px 0 0',
            p: 2,
            overflow: 'hidden',
          },
        }}
      >
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            mb: 2,
            flexShrink: 0,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {detailContent?.type === 'topic' ? '📚 Topic Details' : '📖 Subtopic Details'}
          </Typography>
          <IconButton onClick={() => {
            setDetailDrawerOpen(false);
            setDetailOpenedFromView(false);
          }}>
            <Close />
          </IconButton>
        </Box>
        <Divider sx={{ flexShrink: 0 }} />
        <Box sx={{ 
          mt: 2, 
          overflowY: 'auto', 
          flex: 1,
          pb: 2,
        }}>
          {renderDetailContent()}
        </Box>
      </Drawer>

      {/* Desktop Export Button */}
      {currentData && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            display: { xs: 'none', sm: 'flex' },
            gap: 1,
            zIndex: 100,
          }}
        >
          <Button            variant="contained"
            startIcon={<FileDownload />}
            onClick={handleMenuOpen}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              bgcolor: '#4f46e5',
              '&:hover': { bgcolor: '#4338ca' },
              boxShadow: '0 4px 16px rgba(79, 70, 229, 0.3)',
            }}
          >
            Export
          </Button>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
          >
            <MenuItem onClick={handleExportPerformancePDF}>
              <TimelineIcon sx={{ mr: 1, fontSize: 18 }} />
              Export Performance Table
            </MenuItem>
            <MenuItem onClick={handleExportKnowledgePDF}>
              <TableChart sx={{ mr: 1, fontSize: 18 }} />
              Export Knowledge Map
            </MenuItem>
          </Menu>
        </Box>
      )}
    </Box>
  );
};

export default PerformanceMapViewer;